<template>
  <flow-part :id="$options.name">
    <template #heading>
      <h2>Wanneer wil je dat het contract ingaat?</h2>
    </template>

    <template #content>
      <section v-auto-animate>
        <section v-if="!orderStore.moving_in">
          <FieldLabelWithTooltip
            :label="`Vanaf wanneer wil je dat ${providerName} ${productGasIncluded} gaat leveren?`">
            <template #tooltip>
              <Tooltip position="top-right">
                <template #text>
                  Heb je een lopend contract dat binnenkort afloopt? Kies dan 'Wanneer mijn contract afloopt' en vul de eerstvolgende dag ná de einddatum van je huidige contract in als startdatum.

                  <br><br>
                  Is je huidige contract al verlopen of heb je momenteel een variabel of dynamisch contract? Kies dan 'Zo snel mogelijk'.
                </template>
              </Tooltip>
            </template>
          </FieldLabelWithTooltip>

          <div class="first-big-radio">
            <big-radio-input id="start_date_asap_y" v-model="orderStore.start_date_asap" title="Zo snel mogelijk"
              :value="true" />
          </div>

          <big-radio-input id="start_date_asap_n" v-model="orderStore.start_date_asap"
            title="Wanneer mijn contract afloopt" :value="false" />

          <section class="radio-group-error">
            <FieldError :error="orderStore.errors.start_date_asap" />
          </section>
        </section>

        <section v-if="orderStore.moving_in || orderStore.start_date_asap === false" class="input-datefield-element">
          <start-date-picker id="start_date" v-model="orderStore.start_date"
            v-model:error="orderStore.errors.start_date"
            :label="orderStore.moving_in ? 'Datum sleuteloverdracht' : 'Startdatum nieuwe contract'"
            :max-date="maxMoveInDate" />
        </section>

        <section
          v-if="orderStore.start_date_asap !== undefined && orderStore.start_date_asap !== null && orderStore.start_date_asap !== true">
          <div
            v-if="typeof orderStore.start_date !== 'undefined' && orderStore.product.max_start_date < orderStore.start_date && !orderStore.moving_in"
            class="tip">
            <span>
              Je contract loopt nog aardig lang door. Als je je nu aanmeldt gaat dit contract eerder in dan je
              einddatum.<br>
              Weet je zeker dat je geen overstapboete krijgt van je huidige leverancier? We kunnen je aanmelding gewoon
              verwerken, maar wees hier wel bewust van.
            </span>
          </div>
          <div v-if="orderStore.moving_in && typeof orderStore.start_date == 'undefined'" class="tip">
            <span>
              Ben je al verhuisd? Vul dan morgen in.<br>
              Tot je contract ingaat regelt de netbeheerder je {{ productGasIncluded }}.
            </span>
          </div>
        </section>

        <section v-if="!!orderStore.start_date_asap" class="tip asap-notice">
          <icon icon="check" size="l" />
          <div>
            {{ providerName }} geeft zo snel mogelijk je definitieve ingangsdatum door.
          </div>



        </section>
        <section v-if="!!!orderStore.start_date_asap"
          v-show="orderStore.start_date_asap !== undefined && orderStore.start_date_asap !== null"
          class="tip start-date-notice asap-notice">
          <icon icon="check" size="l" />
          <div>
            {{ providerName }} geeft zo snel mogelijk je definitieve ingangsdatum door.
          </div>
        </section>
      </section>
    </template>
  </flow-part>
</template>

<script>
import { addMonths, parse } from 'date-fns';
import BigRadioInput from 'vue-elements/input/BigRadioInput.vue';
import FormPartMixin from '../../lib/FormPartMixin';
import FieldLabel from '../../../../../abstracts/elements/field/_shared/label/fieldLabel.vue';
import FlowPart from '../FlowPart.vue';
import StartDatePicker from '../StarDatePicker.vue';
import { useOrderStore } from '../../stores/orderStore.js';
import Icon from '../../../../../abstracts/elements/icon/Icon.vue';
import FieldError from '../../../../vue-elements/field/FieldError.vue';
import FieldLabelWithTooltip from '../../../../vue-elements/field/FieldLabelWithTooltip.vue';
import Tooltip from '../../../../../abstracts/elements/tooltip/tooltip.vue';

export default {
  name: 'StartDatePart',
  mixins: [FormPartMixin()],
  components: {
    BigRadioInput,
    StartDatePicker,
    FlowPart,
    FieldError,
    FieldLabel,
    FieldLabelWithTooltip,
    Tooltip,
    Icon
  },
  setup() {
    const orderStore = useOrderStore();

    return {
      orderStore,
    };
  },
  computed: {
    maxMoveInDate() {
      if (this.orderStore.product.max_start_date === null) {
        // If no max start date is set, we default to 12 months from now
        return addMonths(new Date(), 12);
      }

      // If a max start date is set, we parse it and return it as the max date
      return parse(this.orderStore.product.max_start_date, 'yyyy-MM-dd', new Date());
    },
    providerName() {
      return this.orderStore.product.provider_name;
    },
    productGasIncluded() {
      return this.orderStore.product.gas_included
        ? 'stroom en gas'
        : 'stroom';
    },
  },
};
</script>

<style lang="scss" scoped>
@use '../../../../../assets/scss/_variables/colors';
@use '../../../../../assets/scss/_helpers/display';
@use '../../../../../assets/scss/_helpers/breakpoint';
@use '../../style/helpers';

.radio-group.vertical {
  display: grid;
  grid-gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(312px, 1fr));

  @include breakpoint.up(sm) {
    grid-template-columns: repeat(auto-fit, 312px);
    grid-gap: 16px;
  }
}

.big-radio-input-field {
  margin: 16px 0;
}

.first-big-radio .big-radio-input-field {
  margin-top: 8px;
}

.input-datefield-element {
  @include helpers.field-group-spacing-top;
}

.tip {
  line-height: 140%;
  padding: 16px;
  padding-left: 0;
  padding-right: 0;
  font-size: 15px;
  color: colors.$text-subtle;
  margin-top: 16px;

  .bold {
    font-weight: 700;
  }

  p {
    margin-bottom: 8px;
  }

  &.asap-notice {

    position: relative;
    display: flex;
    gap: 18px;
    align-items: center;
    padding-top: 0;
    padding-bottom: 8px;

    background: #f2fffc;
    border-radius: 15px;
    padding: 18px;
    padding-right: 8px;
    color: #0e8a4f;

    margin-top: 24px;

    .icon-element {
      background-color: #15A35F;
    }
  }
}

.datum-input {
  width: 100%;
}
</style>
